import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, Plus, Mail, Trash2, Edit3, X, Crown, Shield, User,
  Send, CheckCircle2, Clock, Target, Lock, RefreshCw,
  ExternalLink, Phone, Globe, MapPin, UserPlus, AlertTriangle,
  MessageSquare, ArrowRightLeft, Check, Inbox, Zap,
  ArrowUpRight, Building2, Sparkles, ChevronRight,
  Star, TrendingUp, Award, Search
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Constants ─────────────────────────────────────────────────────────────
const STATUSES = [
  { value:'pending',     label:'Pending',     cls:'badge-gray'   },
  { value:'in_progress', label:'In Progress', cls:'badge-blue'   },
  { value:'contacted',   label:'Contacted',   cls:'badge-gold'   },
  { value:'qualified',   label:'Qualified',   cls:'badge-purple' },
  { value:'converted',   label:'Converted',   cls:'badge-green'  },
  { value:'lost',        label:'Lost',        cls:'badge-red'    },
];
const PRIORITIES = [
  { value:'urgent', label:'Urgent', color:'#ef4444' },
  { value:'high',   label:'High',   color:'#f97316' },
  { value:'medium', label:'Medium', color:'#f59e0b' },
  { value:'low',    label:'Low',    color:'#94a3b8' },
];
const ROLE_BADGE = { owner:'badge-red', manager:'badge-purple', member:'badge-blue' };
const ROLE_ICON  = { owner:<Crown size={11}/>, manager:<Shield size={11}/>, member:<User size={11}/> };
const statusBadge = s => STATUSES.find(x=>x.value===s)?.cls   || 'badge-gray';
const statusLabel = s => STATUSES.find(x=>x.value===s)?.label || (s||'—');
const priColor    = p => PRIORITIES.find(x=>x.value===p)?.color || '#94a3b8';
const priLabel    = p => PRIORITIES.find(x=>x.value===p)?.label || (p||'—');
const fmtDate     = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—';
const isOverdue   = d => d && new Date(d) < new Date();

// ── Plan helpers (DB-driven) ──────────────────────────────────────────────
const PLAN_COLOR = { free:'var(--text3)', individual:'var(--blue)', pro:'var(--accent)', pro_plus:'var(--purple)', enterprise:'var(--gold)' };
const PLAN_BG    = { free:'var(--bg3)', individual:'var(--blue-bg)', pro:'var(--accent-bg)', pro_plus:'var(--purple-bg)', enterprise:'var(--gold-bg)' };

// Module-level cache — fetches from /subscription/plans (DB) once per session
let _plansCache = null;
let _plansFetching = false;
let _plansCallbacks = [];

function fetchPlansOnce() {
  if (_plansCache) return Promise.resolve(_plansCache);
  if (_plansFetching) return new Promise(res => _plansCallbacks.push(res));
  _plansFetching = true;
  return api.get('/subscription/plans')
    .then(r => {
      const plans = (r.data.plans || []).map(p => ({
        ...p,
        features: typeof p.features === 'string' ? JSON.parse(p.features || '[]') : (p.features || []),
      }));
      _plansCache = plans;
      _plansCallbacks.forEach(cb => cb(plans));
      _plansCallbacks = [];
      return plans;
    })
    .catch(err => {
      console.error('Failed to load plans from DB:', err.message);
      _plansFetching = false; // allow retry
      _plansCallbacks.forEach(cb => cb([]));
      _plansCallbacks = [];
      return [];
    });
}

function usePlans() {
  const [plans, setPlans] = useState(_plansCache || []);
  useEffect(() => {
    if (_plansCache && _plansCache.length) { setPlans(_plansCache); return; }
    fetchPlansOnce().then(p => setPlans(p));
  }, []);
  return plans;
}

// Component: renders plan cards from DB for NoTeamPlanScreen
function DBPlanCards({ onBuy }) {
  const plans = usePlans().filter(p => !['free','individual'].includes(p.code));
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14 }}>
      {plans.map(plan => {
        const color = PLAN_COLOR[plan.code] || 'var(--text3)';
        const feats = typeof plan.features==='string' ? JSON.parse(plan.features||'[]') : plan.features||[];
        return (
          <div key={plan.code} className="card" style={{ padding:'22px 20px', border:`1px solid var(--border)`, cursor:'pointer', transition:'border-color .15s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor=color}
            onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
            <div style={{ fontSize:28, marginBottom:10 }}>{plan.emoji}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:17, fontWeight:800, marginBottom:4 }}>{plan.name}</div>
            <div style={{ fontSize:22, fontWeight:900, color, marginBottom:10 }}>
              {plan.price_monthly === null ? 'Custom' : `₹${plan.price_monthly}/mo`}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:5, fontSize:12.5, marginBottom:16 }}>
              {plan.max_users > 0 && <div style={{ color:'var(--text2)' }}>👥 Up to <strong>{plan.max_users}</strong> members</div>}
              {plan.max_users === -1 && <div style={{ color:'var(--text2)' }}>👥 <strong>Unlimited</strong> members</div>}
              <div style={{ color:'var(--text2)' }}>⚡ <strong>{plan.credits === 99999 ? '∞' : plan.credits}</strong> credits/member</div>
              <div style={{ color:'var(--green)', fontWeight:600 }}>✓ Members join free</div>
            </div>
            {plan.price_monthly !== null
              ? <button className="btn btn-primary btn-full btn-sm" style={{ background:color, borderColor:color }} onClick={()=>onBuy(plan.code)}>
                  Get {plan.name} →
                </button>
              : <a href="mailto:support@prepb.com?subject=Enterprise Plan" className="btn btn-ghost btn-full btn-sm" style={{ textDecoration:'none', justifyContent:'center', display:'flex' }}>
                  <Mail size={13}/> Contact Us
                </a>
            }
          </div>
        );
      })}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────
function PurchaseModal({ plan: planKey, onClose, onSuccess }) {
  const allPlans  = usePlans();
  const plan      = allPlans.find(p => p.code === planKey);
  const [billing, setBilling] = useState('monthly');
  const [step,    setStep]    = useState('confirm');
  const [txnId,   setTxnId]   = useState('');
  const [subId,   setSubId]   = useState('');
  const [payInfo, setPayInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthlyAmt = plan?.price_monthly || 0;
  const annualAmt  = plan?.price_annual ? plan.price_annual * 12 : monthlyAmt * 12;
  const amount     = billing === 'annual' ? annualAmt : monthlyAmt;

  const initiate = async () => {
    setLoading(true);
    try {
      const r = await api.post('/subscription/initiate', { plan: planKey, billing });
      setPayInfo(r.data.payment);
      setSubId(r.data.subscription.id);
      setStep('payment');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to initiate'); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!txnId.trim()) { toast.error('Enter your UPI transaction ID'); return; }
    setLoading(true);
    try {
      await api.post('/subscription/confirm', { subscriptionId: subId, transactionId: txnId.trim() });
      setStep('done');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div>
            <div className="modal-title">{plan?.emoji} Upgrade to {plan?.name}</div>
            <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>Unlock team features for your account</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>

        {step === 'confirm' && <>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ background:'var(--accent-bg)', border:`2px solid ${PLAN_COLOR[planKey]||'var(--accent)'}`, borderRadius:12, padding:'18px 20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                <div>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, marginBottom:10 }}>{plan?.emoji} {plan?.name}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:13.5 }}>
                    {(typeof plan?.features==='string'?JSON.parse(plan.features||'[]'):plan?.features||[]).slice(0,4).map((t,i) => (
                      <div key={i} style={{ display:'flex', gap:7, color:'var(--text2)' }}>
                        <CheckCircle2 size={13} style={{ color:'var(--green)', flexShrink:0, marginTop:1 }}/>{t}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:900, color:PLAN_COLOR[planKey]||'var(--accent)' }}>
                    {billing==='annual' && plan?.price_annual ? `₹${plan.price_annual}/mo` : `₹${plan?.price_monthly}/mo`}
                  </div>
                  {billing==='annual' && plan?.price_annual && <div style={{ fontSize:12, color:'var(--green)', marginTop:2 }}>₹{annualAmt.toLocaleString('en-IN')}/yr · Save 20%</div>}
                </div>
              </div>
            </div>

            {/* Billing toggle */}
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Billing Cycle</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[['monthly','Monthly',null],['annual','Annual','Save 20%']].map(([v,l,badge]) => (
                  <label key={v} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', border:`2px solid ${billing===v?PLAN_COLOR[planKey]||'var(--accent)':'var(--border)'}`, borderRadius:10, cursor:'pointer', background:billing===v?'var(--accent-bg)':'transparent', transition:'all .15s' }}>
                    <input type="radio" name="billing" value={v} checked={billing===v} onChange={()=>setBilling(v)} style={{ accentColor:PLAN_COLOR[planKey]||'var(--accent)' }}/>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13.5 }}>{l}</div>
                      {badge && <div style={{ fontSize:11.5, color:'var(--green)', fontWeight:600 }}>{badge}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ background:'var(--bg3)', borderRadius:9, padding:'12px 14px', fontSize:13, color:'var(--text3)', lineHeight:1.6 }}>
              📋 After clicking "Proceed", you'll receive UPI payment details. Send the payment and submit your transaction ID — we'll activate your plan within <strong style={{ color:'var(--text2)' }}>30 minutes</strong>.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={initiate} disabled={loading} style={{ background:PLAN_COLOR[planKey]||'var(--accent)', borderColor:PLAN_COLOR[planKey]||'var(--accent)' }}>
              {loading ? <><div className="spinner spinner-sm"/>Loading…</> : <>Proceed to Payment <ArrowUpRight size={14}/></>}
            </button>
          </div>
        </>}

        {step === 'payment' && <>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)', borderRadius:10, padding:'10px 14px', fontSize:13, color:'var(--green)', display:'flex', alignItems:'center', gap:8 }}>
              <CheckCircle2 size={15} style={{ flexShrink:0 }}/> Payment details ready — complete the UPI transfer below
            </div>
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
              {[
                ['UPI ID', <strong style={{ cursor:'pointer', color:'var(--accent)' }} onClick={()=>{navigator.clipboard?.writeText(payInfo?.upi_id||''); toast.success('Copied!')}}>{payInfo?.upi_id} 📋</strong>],
                ['Amount', <strong style={{ color:'var(--green)', fontSize:20 }}>₹{payInfo?.amount?.toLocaleString('en-IN')}</strong>],
                ['Note / Description', <span style={{ fontFamily:'monospace', fontSize:12 }}>{payInfo?.upi_note}</span>],
              ].map(([label, val], i) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom: i<2?'1px solid var(--border)':'none' }}>
                  <span style={{ fontSize:13, color:'var(--text3)' }}>{label}</span>
                  <span style={{ fontSize:14 }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={{ background:'var(--bg3)', borderRadius:9, padding:'12px 14px', fontSize:13, color:'var(--text2)', lineHeight:1.8 }}>
              <strong>Steps:</strong><br/>
              1. Open GPay / PhonePe / Paytm / BHIM<br/>
              2. Pay ₹{payInfo?.amount} to <strong>{payInfo?.upi_id}</strong><br/>
              3. Add note: <strong>{payInfo?.upi_note}</strong><br/>
              4. Copy the transaction ID &amp; paste below
            </div>
            <div className="form-group">
              <label className="form-label">UPI Transaction ID *</label>
              <input className="input" placeholder="e.g. T2506121234567890" value={txnId} onChange={e=>setTxnId(e.target.value)} autoFocus/>
              <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>Found in your UPI app under "Transaction History"</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={()=>setStep('confirm')}>← Back</button>
            <button className="btn btn-primary" onClick={confirm} disabled={loading || !txnId.trim()}>
              {loading ? <><div className="spinner spinner-sm"/>Submitting…</> : <><CheckCircle2 size={14}/>Submit Payment</>}
            </button>
          </div>
        </>}

        {step === 'done' && <>
          <div className="modal-body" style={{ textAlign:'center', padding:'36px 24px' }}>
            <div style={{ width:72, height:72, background:'var(--green-bg)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <CheckCircle2 size={34} style={{ color:'var(--green)' }}/>
            </div>
            <h3 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:10 }}>Payment Submitted! 🎉</h3>
            <p style={{ color:'var(--text3)', fontSize:14, lineHeight:1.7 }}>
              Your <strong style={{ color:'var(--text)' }}>{plan?.name}</strong> plan will be activated within <strong>30 minutes</strong> after our team verifies the payment. You'll receive an email confirmation.
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary btn-full" onClick={()=>{ onClose(); onSuccess?.(); }}>Done</button>
          </div>
        </>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// UPGRADE MODAL — DB-driven
// ─────────────────────────────────────────────────────────────────────────
function UpgradeModal({ onClose, currentPlan, onBuy, showIndividual = false }) {
  const plans = usePlans().filter(p => {
    if (p.code === 'free') return false;
    if (p.code === 'individual') return showIndividual; // show individual only when credits are low
    return true;
  });
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">Upgrade Your Plan</div>
            <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>Unlock team features — members join free, no subscription needed</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="modal-body">
          {plans.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px 0' }}><div className="spinner spinner-lg" style={{ margin:'0 auto' }}/></div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:18 }}>
              {plans.map(plan => {
                const color = PLAN_COLOR[plan.code] || 'var(--accent)';
                const feats = typeof plan.features==='string' ? JSON.parse(plan.features||'[]') : plan.features||[];
                const isCurr = currentPlan === plan.code;
                return (
                  <div key={plan.code} style={{ border:`2px solid ${isCurr ? color : 'var(--border)'}`, borderRadius:12, padding:'20px', position:'relative', background: isCurr ? PLAN_BG[plan.code]||'var(--accent-bg)' : 'var(--surface)', transition:'all .2s' }}>
                    {isCurr && <div style={{ position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)', background:color, color:'#fff', fontSize:10, fontWeight:700, padding:'2px 10px', borderRadius:10, whiteSpace:'nowrap' }}>Current Plan</div>}
                    <div style={{ fontSize:24, marginBottom:8 }}>{plan.emoji}</div>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, marginBottom:4 }}>{plan.name}</div>
                    <div style={{ fontSize:20, fontWeight:900, color, marginBottom:14 }}>
                      {plan.price_monthly === null ? 'Custom' : `₹${Number(plan.price_monthly).toLocaleString('en-IN')}/mo`}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6, fontSize:13, marginBottom:16 }}>
                      {feats.slice(0,4).map((f,i) => (
                        <div key={i} style={{ display:'flex', gap:7, color:'var(--text2)' }}>
                          <CheckCircle2 size={13} style={{ color:'var(--green)', flexShrink:0, marginTop:1 }}/>{f}
                        </div>
                      ))}
                    </div>
                    {!isCurr && (
                      <button className="btn btn-primary btn-full" style={{ background:color, borderColor:color }}
                        onClick={() => { onClose(); plan.price_monthly===null ? window.location.href='mailto:support@prepb.com?subject=Enterprise Plan' : onBuy(plan.code); }}>
                        {plan.price_monthly===null ? <><Mail size={13}/>Contact Us</> : <>Get {plan.name} <ArrowUpRight size={13}/></>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)', borderRadius:10, padding:'12px 16px', fontSize:13.5, color:'var(--green)', display:'flex', alignItems:'center', gap:10 }}>
            <CheckCircle2 size={16} style={{ flexShrink:0 }}/>
            <span><strong>Team members don't need a subscription.</strong> They work under your plan for free.</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// NO-PLAN SCREEN — friendly, visual, step-by-step
// ─────────────────────────────────────────────────────────────────────────
function NoTeamPlanScreen({ onUpgrade, onBuy }) {
  const allPlans  = usePlans();
  const firstPaid = allPlans.find(p => p.code === 'pro') || allPlans.find(p => p.price_monthly && p.price_monthly > 0);
  const HOW_IT_WORKS = [
    { step:'01', icon:'🚀', title:'Upgrade your plan', desc:'Subscribe to Pro, Pro Plus, or Enterprise. Your team members join for free under your plan.' },
    { step:'02', icon:'📧', title:'Invite your team', desc:'Send email invitations to colleagues. They register (if needed) and click Accept — done.' },
    { step:'03', icon:'🎯', title:'Assign leads', desc:'Assign saved leads to any team member with due dates, priorities, and notes.' },
    { step:'04', icon:'📊', title:'Track & manage', desc:'See real-time status, add comments, transfer leads, and view full activity logs.' },
  ];

  return (
    <div style={{ maxWidth:800, margin:'0 auto', padding:'0 20px 60px' }}>
      {/* Hero */}
      <div style={{ textAlign:'center', padding:'48px 24px 40px' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'var(--accent-bg)', border:'1px solid var(--accent)', borderRadius:20, padding:'5px 14px', fontSize:12.5, color:'var(--accent)', fontWeight:700, marginBottom:20 }}>
          <Sparkles size={13}/> Team features
        </div>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:36, fontWeight:900, lineHeight:1.2, marginBottom:14 }}>
          Build your sales team<br/>on LeadRadar
        </h1>
        <p style={{ color:'var(--text3)', fontSize:16, lineHeight:1.7, marginBottom:8, maxWidth:520, margin:'0 auto 8px' }}>
          Invite colleagues, assign leads, track progress and close more deals — together.
        </p>
        <p style={{ color:'var(--green)', fontSize:14, fontWeight:600, marginBottom:36 }}>
          ✓ Team members join free — no extra subscription needed
        </p>
        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-primary btn-lg" onClick={()=>onBuy(firstPaid?.code||'pro')} style={{ gap:8 }}>
            <Zap size={16}/> Upgrade to {firstPaid?.name||'Pro'} — {firstPaid?.price_monthly?`₹${firstPaid.price_monthly}/mo`:''}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={onUpgrade}>
            View all plans
          </button>
        </div>
      </div>

      {/* How it works */}
      <div style={{ marginBottom:36 }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, textAlign:'center', marginBottom:24 }}>How it works</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:16 }}>
          {HOW_IT_WORKS.map(s => (
            <div key={s.step} className="card" style={{ padding:'20px 18px', textAlign:'center' }}>
              <div style={{ fontSize:28, marginBottom:10 }}>{s.icon}</div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', letterSpacing:1, marginBottom:6 }}>STEP {s.step}</div>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>{s.title}</div>
              <div style={{ fontSize:12.5, color:'var(--text3)', lineHeight:1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Plan comparison — from DB */}
      <div style={{ marginBottom:32 }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, textAlign:'center', marginBottom:20 }}>Pick your plan</h2>
        <DBPlanCards onBuy={onBuy}/>
      </div>

      {/* FAQ */}
      <div className="card" style={{ padding:'24px' }}>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:800, marginBottom:16 }}>Common questions</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {[
            ['Do my team members need to pay?', 'No. When you subscribe to Pro or higher, your team members work under your plan — completely free. They just register a free account and accept your invitation.'],
            ['What happens to my team if I downgrade?', 'Your teams and members remain, but you won\'t be able to invite new members. Existing assignments stay intact.'],
            ['Can I change my plan later?', 'Yes, you can upgrade anytime. Contact our admin or email support@prepb.com to make changes.'],
          ].map(([q, a]) => (
            <div key={q}>
              <div style={{ fontWeight:600, fontSize:13.5, marginBottom:5 }}>{q}</div>
              <div style={{ fontSize:13, color:'var(--text3)', lineHeight:1.6 }}>{a}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MODAL WRAPPER
// ─────────────────────────────────────────────────────────────────────────
function Modal({ title, subtitle, onClose, children, size='modal-sm' }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size}`}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{title}</div>
            {subtitle && <div style={{ fontSize:12.5, color:'var(--text3)', marginTop:3 }}>{subtitle}</div>}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM CREATE / EDIT
// ─────────────────────────────────────────────────────────────────────────
function TeamModal({ team, onClose, onSave }) {
  const [form, setForm] = useState({ name:team?.name||'', description:team?.description||'', color:team?.color||'#e11d48' });
  const [loading, setLd] = useState(false);
  const COLORS = ['#e11d48','#3b82f6','#10b981','#f59e0b','#8b5cf6','#06b6d4','#f97316','#6366f1'];
  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Team name required'); return; }
    setLd(true);
    try {
      if (team) await api.put(`/teams/${team.id}`, form);
      else await api.post('/teams', form);
      toast.success(team ? 'Team updated' : 'Team created!');
      onSave();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLd(false); }
  };
  return (
    <Modal title={team ? 'Edit Team' : 'Create New Team'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="form-group">
            <label className="form-label">Team Name *</label>
            <input className="input" placeholder="e.g. Mumbai Sales Team" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} autoFocus/>
          </div>
          <div className="form-group">
            <label className="form-label">Description <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <textarea className="textarea" style={{ minHeight:64 }} placeholder="What does this team focus on?" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
          </div>
          <div className="form-group">
            <label className="form-label">Team Colour</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
              {COLORS.map(c => (
                <div key={c} onClick={() => setForm(f=>({...f,color:c}))}
                  style={{ width:30, height:30, borderRadius:'50%', background:c, cursor:'pointer', border:form.color===c?'3px solid var(--text)':'3px solid transparent', transition:'border .15s', boxShadow:form.color===c?'0 0 0 2px var(--bg), 0 0 0 4px '+c:'none' }}/>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><div className="spinner spinner-sm"/>Saving…</> : (team ? 'Save Changes' : 'Create Team')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// INVITE MODAL
// ─────────────────────────────────────────────────────────────────────────
function InviteModal({ teamId, maxMembers, currentCount, onClose, onSave }) {
  const [email, setEmail] = useState('');
  const [role,  setRole]  = useState('member');
  const [loading, setLd]  = useState(false);
  const remaining = maxMembers === Infinity ? '∞' : Math.max(0, maxMembers - currentCount);
  const submit = async e => {
    e.preventDefault();
    setLd(true);
    try { await api.post(`/teams/${teamId}/invite`, { email:email.trim().toLowerCase(), role }); toast.success(`Invited ${email}`); onSave(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed to send invite'); }
    finally { setLd(false); }
  };
  return (
    <Modal title="Invite Team Member" subtitle="They work under your plan — no subscription needed" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)', borderRadius:8, padding:'10px 14px', fontSize:13, color:'var(--green)', display:'flex', alignItems:'center', gap:8 }}>
            <Users size={14} style={{ flexShrink:0 }}/>
            <strong>{remaining}</strong> slot{remaining!==1?'s':''} remaining · Members join completely free
          </div>
          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <div className="input-group">
              <Mail size={14} className="input-icon"/>
              <input className="input" type="email" placeholder="colleague@company.com" value={email} onChange={e=>setEmail(e.target.value)} autoFocus required/>
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:5 }}>
              If they don't have an account yet, they'll be prompted to register for free.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                ['member',  'Member',  'Can view & update their own assigned leads'],
                ['manager', 'Manager', 'Can also invite members & assign leads to others'],
              ].map(([v,l,desc]) => (
                <label key={v} style={{ display:'flex', gap:10, padding:'10px 12px', border:`1.5px solid ${role===v?'var(--accent)':'var(--border)'}`, borderRadius:9, cursor:'pointer', background:role===v?'var(--accent-bg)':'transparent', transition:'all .15s' }}>
                  <input type="radio" name="role" value={v} checked={role===v} onChange={()=>setRole(v)} style={{ accentColor:'var(--accent)', marginTop:2 }}/>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13.5 }}>{l}</div>
                    <div style={{ fontSize:12, color:'var(--text3)' }}>{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><div className="spinner spinner-sm"/>Sending…</> : <><Send size={13}/>Send Invitation</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ASSIGN LEAD MODAL
// ─────────────────────────────────────────────────────────────────────────
function AssignModal({ lead, team, members, onClose, onSave }) {
  const [form, setForm] = useState({ assignedTo:'', note:'', dueDate:'', priority:'medium' });
  const [loading, setLd] = useState(false);
  const activeMembers = members.filter(m => m.status === 'active');
  const submit = async e => {
    e.preventDefault();
    if (!form.assignedTo) { toast.error('Select a member'); return; }
    setLd(true);
    try {
      await api.post('/teams/assign', { savedLeadId:lead.id, assignedTo:form.assignedTo, teamId:team.id, note:form.note, dueDate:form.dueDate||null, priority:form.priority });
      toast.success('Lead assigned!');
      onSave();
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLd(false); }
  };
  return (
    <Modal title="Assign Lead" subtitle={lead.name} onClose={onClose} size="modal-md">
      <form onSubmit={submit}>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'var(--bg3)', borderRadius:9, padding:'12px 14px' }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{lead.name}</div>
            {lead.phone && <div style={{ fontSize:12.5, color:'var(--text3)' }}>📞 {lead.phone}</div>}
            {lead.address && <div style={{ fontSize:12, color:'var(--text3)' }}>📍 {lead.address?.slice(0,60)}</div>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assign To *</label>
              <select className="select" value={form.assignedTo} onChange={e=>setForm(f=>({...f,assignedTo:e.target.value}))} required>
                <option value="">Select member…</option>
                {activeMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.first_name} {m.last_name} ({m.role})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="select" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Due Date <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <input className="input" type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} min={new Date().toISOString().split('T')[0]}/>
          </div>
          <div className="form-group">
            <label className="form-label">Note for assignee <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <textarea className="textarea" style={{ minHeight:64 }} placeholder="Any specific instructions or context…" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))}/>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><div className="spinner spinner-sm"/>Assigning…</> : <><Target size={13}/>Assign Lead</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TRANSFER MODAL
// ─────────────────────────────────────────────────────────────────────────
function TransferModal({ assignment, members, onClose, onSave }) {
  const [transferTo, setTo] = useState('');
  const [note, setNote]     = useState('');
  const [loading, setLd]    = useState(false);
  const submit = async e => {
    e.preventDefault();
    if (!transferTo) { toast.error('Select a member'); return; }
    setLd(true);
    try { await api.post('/teams/transfer', { assignmentId:assignment.id, transferTo, note }); toast.success('Lead transferred!'); onSave(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLd(false); }
  };
  return (
    <Modal title="Transfer Lead" subtitle={`Currently: ${assignment.assigned_to_name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="form-group">
            <label className="form-label">Transfer To *</label>
            <select className="select" value={transferTo} onChange={e=>setTo(e.target.value)} required>
              <option value="">Select member…</option>
              {members.filter(m => m.status==='active' && m.user_id!==assignment.assigned_to).map(m => (
                <option key={m.user_id} value={m.user_id}>{m.first_name} {m.last_name} ({m.role})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Reason / Note <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
            <textarea className="textarea" style={{ minHeight:64 }} placeholder="Why are you transferring this lead?" value={note} onChange={e=>setNote(e.target.value)}/>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><div className="spinner spinner-sm"/>Transferring…</> : <><ArrowRightLeft size={13}/>Transfer Lead</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LEAD DETAIL SIDE PANEL
// ─────────────────────────────────────────────────────────────────────────
function LeadPanel({ lead, team, members, myRole, onClose, onRefresh }) {
  const { user }                   = useAuth();
  const [comments, setComments]    = useState([]);
  const [activity, setActivity]    = useState([]);
  const [newComment, setComment]   = useState('');
  const [posting, setPosting]      = useState(false);
  const [tab, setTab]              = useState('comments');
  const [transferOpen, setTransfer]= useState(false);
  const [statusLoading, setStLd]   = useState(false);
  const bottomRef                  = useRef(null);
  const canManage  = ['owner','manager'].includes(myRole);
  const savedLeadId= lead.saved_lead_id || lead.id;

  const load = useCallback(async () => {
    try {
      const [cr, ar] = await Promise.all([
        api.get('/teams/comments', { params:{ savedLeadId, teamId:team.id } }),
        api.get('/teams/activity',  { params:{ savedLeadId, teamId:team.id } }),
      ]);
      setComments(cr.data.comments||[]);
      setActivity(ar.data.activity||[]);
    } catch {}
  }, [savedLeadId, team.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab==='comments') bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [comments, tab]);

  const changeStatus = async status => {
    setStLd(true);
    try { await api.put(`/teams/assignments/${lead.id}`, { status }); toast.success(`→ ${statusLabel(status)}`); onRefresh(); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setStLd(false); }
  };

  const postComment = async e => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const r = await api.post('/teams/comments', { savedLeadId, teamId:team.id, comment:newComment.trim() });
      setComments(c => [...c, r.data.comment]);
      setComment('');
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setPosting(false); }
  };

  const delComment = async id => {
    try { await api.delete(`/teams/comments/${id}`); setComments(c => c.filter(x => x.id !== id)); }
    catch { toast.error('Failed'); }
  };

  const actIcon = a => ({ assigned:'🎯', transferred:'↔️', status_changed:'🔄', comment_added:'💬' }[a] || '📋');

  return (
    <>
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:199 }} onClick={onClose}/>
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'min(500px,100vw)', background:'var(--surface)', borderLeft:'1px solid var(--border)', zIndex:200, display:'flex', flexDirection:'column', boxShadow:'-8px 0 40px rgba(0,0,0,.2)' }}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', gap:10 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>{lead.name}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              <span className={`badge ${statusBadge(lead.status)}`}><span className="badge-dot"/>{statusLabel(lead.status)}</span>
              <span style={{ fontSize:11.5, fontWeight:700, color:priColor(lead.priority) }}>● {priLabel(lead.priority)}</span>
              {lead.due_date && <span style={{ fontSize:11.5, color:isOverdue(lead.due_date)?'var(--red)':'var(--text3)' }}>📅 {fmtDate(lead.due_date)}</span>}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15}/></button>
        </div>

        {/* Contact info */}
        <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:6 }}>
          {lead.phone   && <div style={{ display:'flex', gap:8, fontSize:13 }}><Phone size={13} style={{ color:'var(--accent)', flexShrink:0, marginTop:1 }}/><a href={`tel:${lead.phone}`} style={{ color:'var(--text2)', textDecoration:'none' }}>{lead.phone}</a></div>}
          {lead.website && <div style={{ display:'flex', gap:8, fontSize:13 }}><Globe size={13} style={{ color:'var(--accent)', flexShrink:0, marginTop:1 }}/><a href={lead.website} target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)', textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.website.replace(/^https?:\/\/(www\.)?/,'').split('/')[0]}</a></div>}
          {lead.address && <div style={{ display:'flex', gap:8, fontSize:12.5, color:'var(--text3)' }}><MapPin size={13} style={{ flexShrink:0, marginTop:1 }}/>{lead.address}</div>}
          {lead.assigned_to_name && <div style={{ fontSize:12.5, color:'var(--text3)' }}>👤 Assigned to <strong style={{ color:'var(--text)' }}>{lead.assigned_to_name}</strong></div>}
        </div>

        {/* Status change */}
        <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Update Status</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {STATUSES.map(s => (
              <button key={s.value} onClick={() => changeStatus(s.value)} disabled={statusLoading}
                className={`badge ${s.cls}`}
                style={{ cursor:'pointer', padding:'5px 11px', border:`2px solid ${lead.status===s.value?'currentColor':'transparent'}`, fontSize:12, opacity:lead.status===s.value?1:0.6, transition:'all .15s' }}>
                {lead.status===s.value && <Check size={10}/>}{s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding:'10px 20px', borderBottom:'1px solid var(--border)', display:'flex', gap:8 }}>
          {canManage && <button className="btn btn-ghost btn-sm" onClick={() => setTransfer(true)}><ArrowRightLeft size={12}/>Transfer</button>}
          {lead.google_maps_url && <a href={lead.google_maps_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration:'none' }}><ExternalLink size={12}/>View on Maps</a>}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)', padding:'0 20px' }}>
          {[['comments', `💬 Comments (${comments.length})`], ['activity', '📋 Activity']].map(([v,l]) => (
            <button key={v} onClick={() => setTab(v)}
              style={{ padding:'10px 16px', border:'none', background:'none', fontWeight:tab===v?700:500, fontSize:13, color:tab===v?'var(--accent)':'var(--text3)', cursor:'pointer', borderBottom:tab===v?'2px solid var(--accent)':'2px solid transparent', marginBottom:-1, transition:'all .15s' }}>
              {l}
            </button>
          ))}
        </div>

        {/* Comments */}
        {tab === 'comments' && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:12 }}>
              {comments.length === 0 && (
                <div style={{ textAlign:'center', color:'var(--text3)', fontSize:13, padding:'32px 0' }}>
                  <MessageSquare size={28} style={{ margin:'0 auto 10px', opacity:.4 }}/>
                  <div>No comments yet</div>
                  <div style={{ fontSize:12, marginTop:4 }}>Be the first to add context or notes</div>
                </div>
              )}
              {comments.map(c => (
                <div key={c.id} style={{ display:'flex', gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontWeight:600, fontSize:13 }}>{c.first_name} {c.last_name}</span>
                      <span style={{ fontSize:11, color:'var(--text3)' }}>{new Date(c.created_at).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
                      {c.user_id===user?.id && <button onClick={() => delComment(c.id)} style={{ marginLeft:'auto', background:'none', border:'none', color:'var(--text3)', cursor:'pointer', padding:2, display:'flex', borderRadius:4 }}><Trash2 size={11}/></button>}
                    </div>
                    <div style={{ background:'var(--bg3)', borderRadius:'4px 12px 12px 12px', padding:'8px 12px', fontSize:13.5, lineHeight:1.5, wordBreak:'break-word' }}>{c.comment}</div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef}/>
            </div>
            <div style={{ padding:'12px 20px', borderTop:'1px solid var(--border)' }}>
              <form onSubmit={postComment} style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <textarea className="textarea" style={{ flex:1, minHeight:40, maxHeight:100, resize:'none', fontSize:13.5 }}
                  placeholder="Add a comment… (Enter to send, Shift+Enter for new line)"
                  value={newComment} onChange={e => setComment(e.target.value)}
                  onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); postComment(e); } }}/>
                <button type="submit" className="btn btn-primary" style={{ padding:'8px 12px', flexShrink:0 }} disabled={posting || !newComment.trim()}>
                  {posting ? <div className="spinner spinner-sm"/> : <Send size={14}/>}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Activity */}
        {tab === 'activity' && (
          <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:12 }}>
            {activity.length === 0 && (
              <div style={{ textAlign:'center', color:'var(--text3)', fontSize:13, padding:'32px 0' }}>
                <Clock size={28} style={{ margin:'0 auto 10px', opacity:.4 }}/>
                <div>No activity yet</div>
              </div>
            )}
            {activity.map(a => (
              <div key={a.id} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0 }}>{actIcon(a.action)}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>
                    <strong>{a.first_name} {a.last_name}</strong>{' '}
                    {a.action==='assigned' && 'assigned this lead'}
                    {a.action==='transferred' && 'transferred this lead'}
                    {a.action==='status_changed' && <><span style={{ color:'var(--text3)' }}>changed status </span><span style={{ textDecoration:'line-through', color:'var(--text3)' }}>{statusLabel(a.old_value)}</span> → <strong>{statusLabel(a.new_value)}</strong></>}
                    {a.action==='comment_added' && 'added a comment'}
                  </div>
                  {a.note && a.action !== 'comment_added' && <div style={{ fontSize:12, color:'var(--text3)', fontStyle:'italic', marginTop:2 }}>"{a.note}"</div>}
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{new Date(a.created_at).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {transferOpen && <TransferModal assignment={lead} members={members} onClose={() => setTransfer(false)} onSave={() => { setTransfer(false); onRefresh(); load(); }}/>}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TEAM DETAIL VIEW
// ─────────────────────────────────────────────────────────────────────────
function TeamDetail({ teamId, onBack }) {
  const { user }                   = useAuth();
  const [data, setData]            = useState(null);
  const [teamLeads, setTeamLeads]  = useState([]);
  const [savedLeads, setSaved]     = useState([]);
  const [loading, setLoading]      = useState(true);
  const [tab, setTab]              = useState('leads');
  const [inviteOpen, setInvite]    = useState(false);
  const [assignLead, setAssign]    = useState(null);
  const [detailLead, setDetail]    = useState(null);
  const [statusF, setStatusF]      = useState('');
  const [memberF, setMemberF]      = useState('');
  const [priorityF, setPriorityF]  = useState('');
  const [removingId, setRemoving]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, lr, sr] = await Promise.all([
        api.get(`/teams/${teamId}`),
        api.get(`/teams/${teamId}/leads`, { params:{ limit:100 } }),
        api.get('/leads/saved', { params:{ limit:200 } }),
      ]);
      setData(tr.data); setTeamLeads(lr.data.leads||[]); setSaved(sr.data.leads||[]);
    } catch(err) { toast.error(err.response?.data?.message || 'Failed to load team'); }
    finally { setLoading(false); }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this team?\nTheir pending assignments will be unassigned.`)) return;
    setRemoving(userId);
    try { await api.delete(`/teams/${teamId}/members/${userId}`); toast.success(`${name} removed`); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setRemoving(null); }
  };

  const cancelInvitation = async (invId, email) => {
    if (!window.confirm(`Cancel invitation to ${email}?`)) return;
    try { await api.delete(`/teams/${teamId}/invitations/${invId}`); toast.success('Invitation cancelled'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const updateRole = async (userId, role) => {
    try { await api.put(`/teams/${teamId}/members/${userId}/role`, { role }); toast.success('Role updated'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div className="empty-state"><div className="spinner spinner-lg"/></div>;
  if (!data)   return null;

  const { team, members, pendingInvites, myRole, planLimits } = data;
  const canManage   = ['owner','manager'].includes(myRole);
  const activeCount = members.filter(m => m.status==='active').length;
  const maxMembers  = planLimits?.maxMembers ?? 0;
  const filteredLeads = teamLeads.filter(l => {
    if (statusF   && l.status !== statusF)      return false;
    if (memberF   && l.assigned_to !== memberF) return false;
    if (priorityF && l.priority !== priorityF)  return false;
    return true;
  });
  const stats = {
    total:     teamLeads.length,
    pending:   teamLeads.filter(l => l.status==='pending').length,
    progress:  teamLeads.filter(l => l.status==='in_progress').length,
    converted: teamLeads.filter(l => l.status==='converted').length,
    overdue:   teamLeads.filter(l => isOverdue(l.due_date) && !['converted','lost'].includes(l.status)).length,
  };

  return (
    <div>
      {/* Back + header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Teams</button>
        <div style={{ width:42, height:42, borderRadius:12, background:team.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>👥</div>
        <div style={{ flex:1, minWidth:0 }}>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, marginBottom:2 }}>{team.name}</h2>
          <div style={{ fontSize:13, color:'var(--text3)', display:'flex', alignItems:'center', gap:8 }}>
            <span>{activeCount} active member{activeCount!==1?'s':''}</span>
            {myRole !== 'owner' && <span className={`badge ${ROLE_BADGE[myRole]}`}>{ROLE_ICON[myRole]} {myRole}</span>}
          </div>
        </div>
        {canManage && (
          <button className="btn btn-primary btn-sm" onClick={() => setInvite(true)} disabled={maxMembers !== Infinity && activeCount >= maxMembers}>
            <UserPlus size={13}/>Invite Member
          </button>
        )}
      </div>

      {/* Limit warning */}
      {canManage && maxMembers !== Infinity && activeCount >= maxMembers && (
        <div style={{ background:'var(--gold-bg)', border:'1px solid var(--gold)', borderRadius:9, padding:'10px 14px', fontSize:13, color:'var(--gold)', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <AlertTriangle size={14} style={{ flexShrink:0 }}/>
          Member limit reached ({activeCount}/{maxMembers}). Upgrade your plan to add more.
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(100px,1fr))', gap:10, marginBottom:16 }}>
        {[
          { label:'Total Leads', val:stats.total,     color:'var(--accent)' },
          { label:'Pending',     val:stats.pending,   color:'var(--text3)'  },
          { label:'In Progress', val:stats.progress,  color:'var(--blue)'   },
          { label:'Converted',   val:stats.converted, color:'var(--green)'  },
          { label:'Overdue',     val:stats.overdue,   color:'var(--red)'    },
        ].map(s => (
          <div key={s.label} className="card card-sm" style={{ textAlign:'center', padding:'12px 8px' }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:s.color }}>{s.val}</div>
            <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom:16 }}>
        <button className={`tab${tab==='leads'?' active':''}`} onClick={() => setTab('leads')}><Target size={14}/>Leads <span className="tab-count">{teamLeads.length}</span></button>
        <button className={`tab${tab==='members'?' active':''}`} onClick={() => setTab('members')}><Users size={14}/>Members <span className="tab-count">{activeCount}</span></button>
        {canManage && <button className={`tab${tab==='assign'?' active':''}`} onClick={() => setTab('assign')}><Plus size={14}/>Assign Leads</button>}
      </div>

      {/* LEADS TAB */}
      {tab === 'leads' && (
        <div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
            <select className="select" style={{ width:150 }} value={statusF} onChange={e => setStatusF(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select className="select" style={{ width:160 }} value={memberF} onChange={e => setMemberF(e.target.value)}>
              <option value="">All Members</option>
              {members.filter(m => m.status==='active').map(m => <option key={m.user_id} value={m.user_id}>{m.first_name} {m.last_name}</option>)}
            </select>
            <select className="select" style={{ width:130 }} value={priorityF} onChange={e => setPriorityF(e.target.value)}>
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {(statusF||memberF||priorityF) && <button className="btn btn-ghost btn-sm" onClick={() => { setStatusF(''); setMemberF(''); setPriorityF(''); }}>Clear</button>}
            <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
          </div>

          {filteredLeads.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize:42 }}>🎯</div>
              <div style={{ fontWeight:700, fontSize:17 }}>No leads assigned yet</div>
              <div style={{ color:'var(--text3)', fontSize:13.5, maxWidth:280, lineHeight:1.6 }}>
                {canManage ? 'Go to the "Assign Leads" tab to assign saved leads to your team members.' : 'Your manager will assign leads to you here.'}
              </div>
              {canManage && <button className="btn btn-primary btn-sm" onClick={() => setTab('assign')}><Plus size={13}/>Assign First Lead</button>}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {filteredLeads.map(lead => (
                <div key={lead.id} className="card" style={{ padding:'14px 16px', cursor:'pointer', borderLeft:`3px solid ${priColor(lead.priority)}`, transition:'box-shadow .15s' }}
                  onClick={() => setDetail(lead)}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{lead.name}</div>
                        <span className={`badge ${statusBadge(lead.status)}`}><span className="badge-dot"/>{statusLabel(lead.status)}</span>
                        <span style={{ fontSize:11, fontWeight:700, color:priColor(lead.priority) }}>● {priLabel(lead.priority)}</span>
                        {isOverdue(lead.due_date) && !['converted','lost'].includes(lead.status) && <span className="badge badge-red"><AlertTriangle size={9}/>Overdue</span>}
                      </div>
                      <div style={{ display:'flex', gap:14, fontSize:12.5, color:'var(--text3)', flexWrap:'wrap' }}>
                        {lead.phone && <span>📞 {lead.phone}</span>}
                        {lead.assigned_to_name && <span>👤 {lead.assigned_to_name}</span>}
                        {lead.due_date && <span style={{ color:isOverdue(lead.due_date)?'var(--red)':'var(--text3)' }}>📅 {fmtDate(lead.due_date)}</span>}
                      </div>
                    </div>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={e => { e.stopPropagation(); setDetail(lead); }} title="Comments & details">
                      <MessageSquare size={14}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* MEMBERS TAB */}
      {tab === 'members' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ background:'var(--green-bg)', border:'1px solid var(--green)', borderRadius:9, padding:'10px 14px', fontSize:13, color:'var(--green)', marginBottom:4, display:'flex', alignItems:'center', gap:8 }}>
            <CheckCircle2 size={14} style={{ flexShrink:0 }}/>
            {activeCount}/{maxMembers===Infinity?'∞':maxMembers} members active · <strong>Members use your plan — no subscription needed from them</strong>
          </div>
          {members.filter(m => m.status !== 'removed').map(m => (
            <div key={m.user_id} className="card" style={{ padding:'14px 16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:'50%', background:`linear-gradient(135deg,${team.color},${team.color}88)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:15, flexShrink:0 }}>
                  {m.first_name?.[0]}{m.last_name?.[0]}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{m.first_name} {m.last_name}</div>
                  <div style={{ fontSize:12.5, color:'var(--text3)' }}>{m.email}{m.job_title?` · ${m.job_title}`:''}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, flexWrap:'wrap' }}>
                  <div style={{ textAlign:'center', minWidth:32 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'var(--accent)' }}>{m.assigned_count||0}</div>
                    <div style={{ fontSize:10.5, color:'var(--text3)' }}>leads</div>
                  </div>
                  <span className={`badge ${ROLE_BADGE[m.role]}`}>{ROLE_ICON[m.role]} {m.role}</span>
                  {m.status==='invited' && <span className="badge badge-gold"><Clock size={10}/>Pending</span>}
                  {myRole==='owner' && m.user_id!==user?.id && m.role!=='owner' && (
                    <select className="select" style={{ fontSize:12, padding:'3px 8px', height:28, width:'auto' }} value={m.role} onChange={e => updateRole(m.user_id, e.target.value)}>
                      <option value="member">Member</option>
                      <option value="manager">Manager</option>
                    </select>
                  )}
                  {canManage && m.user_id!==user?.id && m.role!=='owner' && (
                    <button className="btn btn-danger btn-sm" disabled={removingId===m.user_id} onClick={() => removeMember(m.user_id, `${m.first_name} ${m.last_name}`)}>
                      {removingId===m.user_id ? <div className="spinner spinner-sm"/> : <Trash2 size={13}/>} Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {pendingInvites.length > 0 && (
            <div style={{ marginTop:8 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>
                Pending Invitations ({pendingInvites.length})
              </div>
              {pendingInvites.map(inv => (
                <div key={inv.id} className="card card-sm" style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, padding:'10px 14px' }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Mail size={14} style={{ color:'var(--text3)' }}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.email}</div>
                    <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:1 }}>Invited as {inv.role} · Expires {new Date(inv.expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</div>
                  </div>
                  <span className="badge badge-gold"><Clock size={10}/> Awaiting</span>
                  {canManage && (
                    <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--red)', flexShrink:0 }} onClick={() => cancelInvitation(inv.id, inv.email)} title="Cancel invitation">
                      <X size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {members.filter(m => m.status!=='removed').length === 0 && pendingInvites.length === 0 && (
            <div className="empty-state">
              <div style={{ fontSize:40 }}>👤</div>
              <div style={{ fontWeight:600 }}>No members yet</div>
              <div style={{ fontSize:13, color:'var(--text3)' }}>Click "Invite Member" to add your first team member.</div>
              {canManage && <button className="btn btn-primary btn-sm" onClick={() => setInvite(true)}><UserPlus size={13}/>Invite First Member</button>}
            </div>
          )}
        </div>
      )}

      {/* ASSIGN LEADS TAB */}
      {tab === 'assign' && canManage && (
        <div>
          <div style={{ fontSize:13.5, color:'var(--text3)', marginBottom:14 }}>
            Select a saved lead to assign to a team member. The member will be notified by email.
          </div>
          {savedLeads.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize:40 }}>📋</div>
              <div style={{ fontWeight:600, fontSize:16 }}>No saved leads</div>
              <div style={{ fontSize:13, color:'var(--text3)', marginBottom:14 }}>Save leads from the Search page first, then come back here to assign them.</div>
              <a href="/search" className="btn btn-primary btn-sm" style={{ textDecoration:'none' }}><Search size={13}/>Go to Search</a>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
              {savedLeads.map(lead => (
                <div key={lead.id} className="card card-hover" style={{ padding:'14px 16px' }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:5 }}>{lead.name}</div>
                  {lead.phone   && <div style={{ fontSize:12.5, color:'var(--text3)', marginBottom:2 }}>📞 {lead.phone}</div>}
                  {lead.address && <div style={{ fontSize:12, color:'var(--text3)', marginBottom:12 }}>📍 {(lead.address||'').slice(0,50)}</div>}
                  <button className="btn btn-primary btn-full btn-sm" onClick={() => setAssign(lead)}>
                    <Target size={13}/>Assign to Member
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {inviteOpen  && <InviteModal teamId={teamId} maxMembers={maxMembers===Infinity?Infinity:maxMembers} currentCount={activeCount} onClose={() => setInvite(false)} onSave={() => { setInvite(false); load(); }}/>}
      {assignLead  && <AssignModal lead={assignLead} team={team} members={members} onClose={() => setAssign(null)} onSave={() => { setAssign(null); load(); }}/>}
      {detailLead  && <LeadPanel  lead={detailLead}  team={team} members={members} myRole={myRole} onClose={() => setDetail(null)} onRefresh={load}/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MY ASSIGNED LEADS
// ─────────────────────────────────────────────────────────────────────────
function MyAssignedLeads() {
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('');
  const [detail, setDetail]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/teams/assignments/mine', { params:{ ...(statusF&&{status:statusF}), limit:100 } }); setLeads(r.data.leads||[]); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, [statusF]);

  useEffect(() => { load(); }, [load]);

  const quickStatus = async (id, status) => {
    try { await api.put(`/teams/assignments/${id}`, { status }); toast.success(`→ ${statusLabel(status)}`); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="empty-state"><div className="spinner spinner-lg"/></div>;

  return (
    <div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14, alignItems:'center' }}>
        <select className="select" style={{ width:160 }} value={statusF} onChange={e => setStatusF(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
        <span style={{ fontSize:13, color:'var(--text3)', marginLeft:'auto' }}>{leads.length} lead{leads.length!==1?'s':''}</span>
      </div>

      {leads.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize:42 }}>📬</div>
          <div style={{ fontWeight:700, fontSize:17 }}>No leads assigned to you</div>
          <div style={{ color:'var(--text3)', fontSize:13.5, maxWidth:300, lineHeight:1.6 }}>
            When your team manager assigns leads to you, they'll appear here. You can update their status and add comments.
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {leads.map(lead => (
            <div key={lead.id} className="card" style={{ padding:'14px 16px', borderLeft:`3px solid ${priColor(lead.priority)}` }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:10 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{lead.name}</div>
                    <span className={`badge ${statusBadge(lead.status)}`}><span className="badge-dot"/>{statusLabel(lead.status)}</span>
                    {isOverdue(lead.due_date) && <span className="badge badge-red"><AlertTriangle size={9}/>Overdue</span>}
                  </div>
                  <div style={{ display:'flex', gap:14, fontSize:12.5, color:'var(--text3)', flexWrap:'wrap' }}>
                    {lead.phone && <span>📞 {lead.phone}</span>}
                    {lead.team_name && <span>👥 {lead.team_name}</span>}
                    {lead.due_date && <span style={{ color:isOverdue(lead.due_date)?'var(--red)':'var(--text3)' }}>📅 {fmtDate(lead.due_date)}</span>}
                    {lead.assigned_by_name && <span>By {lead.assigned_by_name}</span>}
                  </div>
                </div>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setDetail(lead)} title="Comments"><MessageSquare size={14}/></button>
              </div>
              {lead.note && <div style={{ fontSize:12.5, background:'var(--bg3)', borderRadius:7, padding:'6px 10px', marginBottom:10, color:'var(--text2)' }}>💬 {lead.note}</div>}
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                {STATUSES.map(s => (
                  <button key={s.value} onClick={() => quickStatus(lead.id, s.value)}
                    className={`badge ${s.cls}`}
                    style={{ cursor:'pointer', padding:'4px 9px', fontSize:11.5, border:`2px solid ${lead.status===s.value?'currentColor':'transparent'}`, opacity:lead.status===s.value?1:0.55, transition:'opacity .15s' }}>
                    {lead.status===s.value && <Check size={9}/>}{s.label}
                  </button>
                ))}
                {lead.google_maps_url && (
                  <a href={lead.google_maps_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration:'none', marginLeft:'auto', fontSize:12 }}>
                    <ExternalLink size={12}/>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <LeadPanel lead={detail}
          team={{ id:detail.team_id||'', name:detail.team_name||'Team', color:'#e11d48' }}
          members={[]} myRole="member"
          onClose={() => setDetail(null)} onRefresh={load}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MAIN TEAM PAGE
// ─────────────────────────────────────────────────────────────────────────
export default function TeamPage() {
  const { user }                    = useAuth();
  const dbPlans                     = usePlans();  // ← plans from DB table
  const [teams, setTeams]           = useState([]);
  const [memberTeams, setMemberTeams]= useState([]);
  const [loading, setLoading]       = useState(true);
  const [planLimits, setPlanLimits] = useState({});
  const [createOpen, setCreate]     = useState(false);
  const [editTeam, setEdit]         = useState(null);
  const [activeTeam, setActive]     = useState(null);
  const [tab, setTab]               = useState('teams');
  const [upgradeOpen, setUpgrade]   = useState(false);
  const [buyPlan, setBuyPlan]       = useState(null);

  const PAID = ['pro','pro_plus','enterprise'].includes(user?.plan);
  // DB-driven plan info for the current user
  const myPlan        = dbPlans.find(p => p.code === (user?.plan || 'free'));
  const myMaxTeams    = myPlan?.max_teams    ?? planLimits.maxTeams   ?? 0;
  const myMaxMembers  = myPlan?.max_users    ?? planLimits.maxMembers ?? 0;
  const myPlanLabel   = myPlan?.name         ?? (user?.plan || 'free');
  const myPlanPrice   = myPlan?.price_monthly;
  // First paid plan that includes team features (for upgrade CTA)
  const firstTeamPlan = dbPlans.find(p => (p.max_teams > 0 || p.max_teams === -1) && p.price_monthly !== null);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const r   = await api.get('/teams');
      const all = r.data.teams || [];
      setTeams(all.filter(t => t.my_role === 'owner'));
      setMemberTeams(all.filter(t => t.my_role !== 'owner'));
      setPlanLimits(r.data.planLimits || {});
    } catch { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const deleteTeam = async id => {
    if (!window.confirm('Delete this team? All lead assignments will be permanently lost.')) return;
    try { await api.delete(`/teams/${id}`); toast.success('Team deleted'); loadTeams(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  // ── No paid plan AND not a member of anyone's team — show full onboarding screen
  if (!PAID && memberTeams.length === 0 && !loading) {
    return (
      <>
        <NoTeamPlanScreen onUpgrade={() => setUpgrade(true)} onBuy={p => setBuyPlan(p)}/>
        {upgradeOpen && <UpgradeModal currentPlan={user?.plan} onClose={() => setUpgrade(false)} onBuy={p => { setUpgrade(false); setBuyPlan(p); }}/>}
        {buyPlan && <PurchaseModal plan={buyPlan} onClose={() => setBuyPlan(null)} onSuccess={loadTeams}/>}
      </>
    );
  }

  // ── Active team detail view
  if (activeTeam) {
    return (
      <div style={{ padding:'20px 24px' }}>
        <TeamDetail teamId={activeTeam} onBack={() => { setActive(null); loadTeams(); }}/>
      </div>
    );
  }

  return (
    <div style={{ padding:'20px 24px' }}>
      <style>{`
        .team-card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
        @media(max-width:600px){ .team-card-grid{grid-template-columns:1fr;} }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:4 }}>Team Management</h1>
          <p style={{ color:'var(--text3)', fontSize:14 }}>
            {PAID
              ? <><strong style={{ color:'var(--green)' }}>Members don't need a subscription</strong> — they work as employees under your {myPlanLabel} plan.</>
              : 'You can see teams you\'ve been added to. Upgrade to create your own teams.'}
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setUpgrade(true)}><ArrowUpRight size={13}/>Upgrade Plan</button>
          {PAID && <button className="btn btn-primary" onClick={() => setCreate(true)}><Plus size={14}/>New Team</button>}
        </div>
      </div>

      {/* Plan info bar — from plans table */}
      <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <span className={`badge ${PAID?ROLE_BADGE.owner:'badge-gray'}`} style={{ background: PLAN_COLOR[user?.plan||'free'], color:'#fff', textTransform:'uppercase' }}>
          {myPlanLabel}
        </span>
        <span style={{ fontSize:13, color:'var(--text3)' }}>
          {PAID
            ? `${myMaxTeams === -1 ? 'Unlimited' : myMaxTeams} team${myMaxTeams !== 1 ? 's' : ''} · ${myMaxMembers === -1 ? 'Unlimited' : myMaxMembers} members per team`
            : firstTeamPlan
              ? `Upgrade to ${firstTeamPlan.name} (₹${firstTeamPlan.price_monthly}/mo) to create teams`
              : 'Upgrade to create teams'
          }
        </span>
        {PAID && teams.length > 0 && (
          <span style={{ fontSize:12.5, color:'var(--text3)', marginLeft:'auto' }}>
            {teams.length}/{myMaxTeams === -1 ? '∞' : myMaxTeams} teams used
          </span>
        )}
        {!PAID && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }} onClick={() => firstTeamPlan ? setBuyPlan(firstTeamPlan.code) : setUpgrade(true)}>
            <Zap size={13}/> {firstTeamPlan ? `Upgrade — ₹${firstTeamPlan.price_monthly}/mo` : 'Upgrade Plan'}
          </button>
        )}
      </div>

      {/* Tabs — smart: only show relevant tabs */}
      <div className="tabs" style={{ marginBottom:20 }}>
        {PAID && <button className={`tab${tab==='teams'?' active':''}`} onClick={() => setTab('teams')}><Crown size={14}/>My Teams <span className="tab-count">{teams.length}</span></button>}
        {memberTeams.length > 0 && <button className={`tab${tab==='member'?' active':''}`} onClick={() => setTab('member')}><Building2 size={14}/>Member Of <span className="tab-count">{memberTeams.length}</span></button>}
        <button className={`tab${tab==='mine'?' active':''}`} onClick={() => setTab('mine')}><Inbox size={14}/>My Assignments</button>
      </div>

      {/* MY TEAMS TAB */}
      {tab === 'teams' && PAID && (
        loading
          ? <div className="empty-state"><div className="spinner spinner-lg"/></div>
          : teams.length === 0
            ? (
              <div className="empty-state">
                <div style={{ fontSize:52 }}>👥</div>
                <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:20 }}>Create your first team</div>
                <div style={{ color:'var(--text3)', fontSize:14, maxWidth:320, lineHeight:1.7 }}>
                  Invite your sales colleagues, assign them leads, and track progress together.
                  Your team members join for <strong>free</strong>.
                </div>
                <button className="btn btn-primary" onClick={() => setCreate(true)}><Plus size={14}/>Create First Team</button>
              </div>
            )
            : (
              <div className="team-card-grid">
                {teams.map(team => (
                  <div key={team.id} className="card card-hover" style={{ cursor:'pointer', borderTop:`3px solid ${team.color}` }} onClick={() => setActive(team.id)}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                      <div style={{ width:44, height:44, borderRadius:12, background:team.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>👑</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{team.name}</div>
                        {team.description && <div style={{ fontSize:12.5, color:'var(--text3)', lineHeight:1.4 }}>{team.description}</div>}
                      </div>
                      <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEdit(team)} title="Edit"><Edit3 size={13}/></button>
                        <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--red)' }} onClick={() => deleteTeam(team.id)} title="Delete"><Trash2 size={13}/></button>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:14, fontSize:13, color:'var(--text3)', borderTop:'1px solid var(--border)', paddingTop:12 }}>
                      <span>👥 <strong style={{ color:'var(--text)' }}>{team.member_count||0}</strong> members</span>
                      <span>🎯 <strong style={{ color:'var(--text)' }}>{team.lead_count||0}</strong> leads</span>
                      <span style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:4, color:'var(--accent)', fontSize:12, fontWeight:600 }}>
                        View <ChevronRight size={12}/>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
      )}

      {/* MEMBER OF TAB */}
      {tab === 'member' && (
        loading
          ? <div className="empty-state"><div className="spinner spinner-lg"/></div>
          : memberTeams.length === 0
            ? <div className="empty-state"><div style={{ fontSize:42 }}>👥</div><div style={{ fontWeight:600 }}>Not a member of any team yet</div></div>
            : (
              <div>
                <div style={{ fontSize:13.5, color:'var(--text3)', marginBottom:14, display:'flex', alignItems:'center', gap:8 }}>
                  <CheckCircle2 size={14} style={{ color:'var(--green)' }}/>
                  You've been added to {memberTeams.length} team{memberTeams.length!==1?'s':''} as an employee. No subscription needed.
                </div>
                <div className="team-card-grid">
                  {memberTeams.map(team => (
                    <div key={team.id} className="card card-hover" style={{ cursor:'pointer', borderTop:`3px solid ${team.color}` }} onClick={() => setActive(team.id)}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                        <div style={{ width:44, height:44, borderRadius:12, background:team.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>👥</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{team.name}</div>
                          <span className={`badge ${ROLE_BADGE[team.my_role]}`}>{ROLE_ICON[team.my_role]} {team.my_role}</span>
                        </div>
                      </div>
                      {team.description && <div style={{ fontSize:12.5, color:'var(--text3)', marginBottom:10, lineHeight:1.4 }}>{team.description}</div>}
                      <div style={{ display:'flex', gap:14, fontSize:13, color:'var(--text3)', borderTop:'1px solid var(--border)', paddingTop:12 }}>
                        <span>👥 <strong style={{ color:'var(--text)' }}>{team.member_count||0}</strong> members</span>
                        <span>🎯 <strong style={{ color:'var(--text)' }}>{team.lead_count||0}</strong> leads</span>
                        <span style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:4, color:'var(--accent)', fontSize:12, fontWeight:600 }}>
                          View <ChevronRight size={12}/>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
      )}

      {tab === 'mine' && <MyAssignedLeads/>}

      {createOpen && <TeamModal onClose={() => setCreate(false)} onSave={() => { setCreate(false); loadTeams(); }}/>}
      {editTeam   && <TeamModal team={editTeam} onClose={() => setEdit(null)} onSave={() => { setEdit(null); loadTeams(); }}/>}
      {upgradeOpen && <UpgradeModal currentPlan={user?.plan} onClose={() => setUpgrade(false)} onBuy={p => { setUpgrade(false); setBuyPlan(p); }}/>}
      {buyPlan && <PurchaseModal plan={buyPlan} onClose={() => setBuyPlan(null)} onSuccess={loadTeams}/>}
    </div>
  );
}